import React, { useState, useEffect, useCallback } from "react";
import Box from "../Dashboard/components/Box";
import "../Dashboard/dashboard.css";
import "./superadmin.css";
import Loader from "../../../components/Loader";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, authFetch } from "../../../config/api";
import { useSettings } from "../../../hooks/useSettings";
import { runFullSync, getLastSyncTime } from "../../../utils/syncManager";
import { fetchSessions as fetchSessionsUtil } from "../../../utils/fetchSessions";
import { getCatalogue } from "../../../utils/offlineQuery";

function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [load, setLoad] = useState(false);
  const [current_session, setCurrent_session] = useState("");
  const [total_sessions, setTotal_sessions] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({ departments: 0, courses: 0, users: 0 });

  // Correct semester levels modal
  const [showCorrectModal, setShowCorrectModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState("");
  const [selectedSemester, setSelectedSemester] = useState("");
  const [correcting, setCorrecting] = useState(false);
  const [correctStatus, setCorrectStatus] = useState({ type: "", text: "" });

  // Transcript settings modal
  const { settings, invalidate } = useSettings();
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    faculty_officer_name: "",
    dean_name: "",
  });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsStatus, setSettingsStatus] = useState({ type: "", text: "" });
  const [sigUploading, setSigUploading] = useState({
    faculty_officer_signature: false,
    dean_signature: false,
  });

  const [backingUp, setBackingUp] = useState(false);
  const [showRecalcConfirm, setShowRecalcConfirm] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcStatus, setRecalcStatus] = useState({ type: "", text: "" });
  const [recalcProgress, setRecalcProgress] = useState({ phase: "", current: 0, total: 0 });

  // Offline sync
  const [syncing, setSyncing] = useState(false);
  const [syncStep, setSyncStep] = useState("");
  const [syncProgress, setSyncProgress] = useState(0); // 0–1
  const [lastSync, setLastSync] = useState(null);
  const [syncError, setSyncError] = useState("");

  useEffect(() => {
    getLastSyncTime().then(setLastSync);
  }, []);

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncError("");
    setSyncStep("Starting sync…");
    setSyncProgress(0);
    const ok = await runFullSync(
      (msg, stepIndex, total, progress) => {
        setSyncStep(msg);
        setSyncProgress((stepIndex + progress) / total);
      },
      (err) => setSyncError(err.message),
    );
    if (ok) {
      setLastSync(Date.now());
      setSyncStep("Sync complete ✓");
    }
    setSyncing(false);
  };

  const handleDownloadBackup = async () => {
    setBackingUp(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/backup`);
      if (!res.ok) throw new Error("Backup failed.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setBackingUp(false);
    }
  };

  const handleRecalculateGpa = () => {
    setRecalculating(true);
    setRecalcStatus({ type: "", text: "" });
    setRecalcProgress({ phase: "", current: 0, total: 0, pct: 0 });

    const token = localStorage.getItem("token");
    const es = new EventSource(
      `${API_BASE_URL}/api/class/recalculate-gpa/stream?token=${token}`,
    );

    es.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.phase === "done") {
        es.close();
        setRecalculating(false);
        setRecalcProgress({ phase: "done", current: 0, total: 0, pct: 100 });
        setRecalcStatus({
          type: "success",
          text: `Done — ${data.semesterRecords} semester records and ${data.students} students updated.`,
        });
      } else if (data.phase === "error") {
        es.close();
        setRecalculating(false);
        setRecalcProgress({ phase: "", current: 0, total: 0, pct: 0 });
        setRecalcStatus({ type: "error", text: data.message });
      } else {
        setRecalcProgress(data);
      }
    };

    es.onerror = () => {
      es.close();
      setRecalculating(false);
      setRecalcProgress({ phase: "", current: 0, total: 0 });
      setRecalcStatus({ type: "error", text: "Connection lost. Please try again." });
    };
  };

  const name = localStorage.getItem("name") || "Super Admin";

  useEffect(() => {
    if (settings) {
      setSettingsForm({
        faculty_officer_name: settings.faculty_officer_name || "",
        dean_name: settings.dean_name || "",
      });
    }
  }, [settings]);

  const fetchData = useCallback(async () => {
    setLoad(true);
    try {
      let sessJson, deptJson, courseJson;

      // Try live API first, fall back to IndexedDB on any failure
      try {
        const [sessRes, deptRes, courseRes, userRes] = await Promise.all([
          authFetch(`${API_BASE_URL}/api/sessions/`),
          authFetch(`${API_BASE_URL}/api/catalogue/departments`),
          authFetch(`${API_BASE_URL}/api/catalogue/courses`),
          authFetch(`${API_BASE_URL}/api/users`),
        ]);
        if (!sessRes.ok) throw new Error(`sessions ${sessRes.status}`);
        const sessText = await sessRes.text();
        if (sessText.trim().startsWith("<")) throw new Error("HTML response");
        sessJson = JSON.parse(sessText);
        deptJson = await deptRes.json();
        courseJson = await courseRes.json();
        const userJson = await userRes.json();
        setStats((prev) => ({ ...prev, users: userJson.users?.length || 0 }));
      } catch {
        const [sessResult, catResult] = await Promise.all([
          fetchSessionsUtil(),
          getCatalogue(),
        ]);
        sessJson = sessResult;
        deptJson = { departments: catResult.departments || [] };
        courseJson = { courses: catResult.courses || [] };
      }

      if (sessJson.sessions) {
        setSessions(sessJson.sessions);
        const current = sessJson.sessions.find((s) => s.current === true);
        setCurrent_session(current?.session || "");
        setTotal_sessions(sessJson.sessions.length);
        setSelectedSession((prev) => prev || current?.session || "");
      }
      setStats((prev) => ({
        ...prev,
        departments: deptJson.departments?.length || 0,
        courses: courseJson.courses?.length || 0,
      }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoad(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCorrectSemesterLevels = async () => {
    if (!selectedSession || !selectedSemester) return;
    setCorrecting(true);
    setCorrectStatus({ type: "", text: "" });
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/student/correct-semester-levels`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session: selectedSession,
            semester: Number(selectedSemester),
          }),
        },
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Failed");
      setCorrectStatus({
        type: "success",
        text: `${json.message} Updated ${json.updated} of ${json.total} records.`,
      });
    } catch (err) {
      setCorrectStatus({ type: "error", text: err.message });
    } finally {
      setCorrecting(false);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    setSettingsStatus({ type: "", text: "" });
    try {
      const res = await authFetch(`${API_BASE_URL}/api/settings/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settingsForm),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to save");
      invalidate();
      setSettingsStatus({ type: "success", text: "Settings saved." });
      setTimeout(() => {
        setShowSettingsModal(false);
        setSettingsStatus({ type: "", text: "" });
      }, 800);
    } catch (err) {
      setSettingsStatus({ type: "error", text: err.message });
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleSignatureUpload = async (field, file) => {
    if (!file) return;
    setSigUploading((prev) => ({ ...prev, [field]: true }));
    setSettingsStatus({ type: "", text: "" });
    try {
      const formData = new FormData();
      formData.append("signature", file);
      const res = await authFetch(
        `${API_BASE_URL}/api/settings/signature/${field}`,
        {
          method: "POST",
          body: formData,
        },
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Upload failed");
      invalidate();
      setSettingsStatus({ type: "success", text: "Signature uploaded." });
      setTimeout(() => {
        setShowSettingsModal(false);
        setSettingsStatus({ type: "", text: "" });
      }, 800);
    } catch (err) {
      setSettingsStatus({ type: "error", text: err.message });
    } finally {
      setSigUploading((prev) => ({ ...prev, [field]: false }));
    }
  };

  return (
    <div className="admin_dashboard">
      <div className="admin_dashboard_header">
        <div>
          <p className="admin_dashboard_overline">Super Admin</p>
          <h2>Welcome, {name}</h2>
          <p className="admin_dashboard_subtitle">
            Full system control — manage users, courses, departments, students
            and results.
          </p>
        </div>
        <div className="admin_dashboard_actions">
          <button className="dash_btn" onClick={() => navigate("/admin/users")}>
            User Management
          </button>
          <button
            className="dash_btn ghost"
            onClick={() => navigate("/admin/catalogue")}
          >
            Course Catalogue
          </button>
          <button
            className="dash_btn ghost"
            onClick={() => setShowCorrectModal(true)}
          >
            Correct Semester Levels
          </button>
          <button
            className="dash_btn ghost"
            onClick={() => setShowSettingsModal(true)}
          >
            Transcript Settings
          </button>
          <button
            className="dash_btn ghost"
            onClick={handleDownloadBackup}
            disabled={backingUp}
          >
            {backingUp ? "Preparing..." : "Download Backup"}
          </button>
          <button
            className="dash_btn ghost"
            onClick={() => setShowRecalcConfirm(true)}
            disabled={recalculating}
          >
            {recalculating ? "Recalculating..." : "Recalculate All GPA"}
          </button>
          <button
            className="dash_btn ghost"
            onClick={handleSync}
            disabled={syncing || !navigator.onLine}
            title={
              !navigator.onLine
                ? "Connect to internet to sync"
                : "Download all data for offline use"
            }
          >
            {syncing ? "Syncing…" : "Sync for Offline"}
          </button>
        </div>
      </div>

      {/* ── Offline sync progress ── */}
      {(syncing || syncStep) && (
        <div className="sync_progress_card">
          <div className="sync_progress_header">
            <span className="sync_progress_label">{syncStep}</span>
            {lastSync && !syncing && (
              <span className="sync_last_time">
                Last synced: {new Date(lastSync).toLocaleString()}
              </span>
            )}
          </div>
          <div className="sync_progress_bar_track">
            <div
              className="sync_progress_bar_fill"
              style={{ width: `${Math.round(syncProgress * 100)}%` }}
            />
          </div>
          {syncError && <p className="sync_error">{syncError}</p>}
        </div>
      )}
      {!syncing && lastSync && !syncStep.includes("Syncing") && (
        <div className="sync_last_synced">
          ✓ Offline data last synced: {new Date(lastSync).toLocaleString()}
        </div>
      )}

      {recalculating && (
        <div className="recalc_progress_card">
          <div className="sync_progress_header">
            <span className="recalc_progress_label">
              {recalcProgress.phase === "fetching" && (recalcProgress.step || "Loading data from database…")}
              {recalcProgress.phase === "start" && "Starting…"}
              {recalcProgress.phase === "semester" && `Step 1/2 — Semester & session GPA: ${recalcProgress.current} / ${recalcProgress.total} records`}
              {recalcProgress.phase === "cgpa" && `Step 2/2 — Overall CGPA: ${recalcProgress.current} / ${recalcProgress.total} students`}
              {(!recalcProgress.phase || recalcProgress.phase === "") && "Starting recalculation…"}
            </span>
            <span className="recalc_progress_pct">
              {recalcProgress.pct > 0 ? `${recalcProgress.pct}%` : ""}
            </span>
          </div>
          <div className="sync_progress_bar_track">
            <div
              className="recalc_progress_bar_fill"
              style={{ width: `${recalcProgress.pct || 2}%` }}
            />
          </div>
        </div>
      )}

      {recalcStatus.text && (
        <div
          className={`dash_modal_status ${recalcStatus.type}`}
          style={{
            margin: "0 0 1rem",
            borderRadius: 8,
            padding: "0.6rem 1rem",
          }}
        >
          {recalcStatus.text}
          <button
            onClick={() => setRecalcStatus({ type: "", text: "" })}
            style={{
              marginLeft: "1rem",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            ×
          </button>
        </div>
      )}

      <div className="sa_stats_grid">
        <div className="stat_card">
          <p>Current Session</p>
          <h3>{current_session || "—"}</h3>
        </div>
        <div className="stat_card">
          <p>Total Sessions</p>
          <h3>{total_sessions}</h3>
        </div>
        <div className="stat_card">
          <p>Departments</p>
          <h3>{stats.departments}</h3>
        </div>
        <div className="stat_card">
          <p>Courses in Catalogue</p>
          <h3>{stats.courses}</h3>
        </div>
        <div className="stat_card">
          <p>Admin Users</p>
          <h3>{stats.users}</h3>
        </div>
      </div>

      {load && <Loader />}

      <p className="sa_section_label">Admin Tools</p>
      <div className="admin_dashboard_boxes">
        <Box title="Course Catalogue" color="purple" url="/admin/catalogue" />
        <Box title="User Management" color="indigo" url="/admin/users" />
        <Box
          title="Student Management"
          color="green"
          url="/admin/student/management"
        />
        <Box
          title="Duplicate Students"
          color="gray"
          url="/duplicate-students"
        />
      </div>

      <p className="sa_section_label">Academic Tools</p>
      <div className="admin_dashboard_boxes">
        <Box
          title="Course Registration"
          color="green"
          url="/admin/course-reg/sessions"
        />
        <Box title="Results" color="gray" url="/admin/results/sessions" />
        <Box
          title="Faculty Courses"
          color="brown"
          url="/admin/faculty/courses"
        />
        <Box
          title="Non-Pharmacy Courses"
          color="blue"
          url="/admin/faculty/external"
        />
        <Box
          title="Probation Lists"
          color="red"
          url="/admin/faculty/probation/sessions"
        />
        <Box
          title="Error Students"
          color="error"
          url="/admin/faculty/error/students/sessions"
        />
        <Box
          title="Outstanding Failed Courses"
          color="finalists"
          url="/admin/ofc"
        />
        <Box
          title="Professionals"
          color="professionals"
          url="/admin/professionals/sessions"
        />
        <Box title="Top Students" color="brown" url="/admin/topstudents" />
      </div>

      {/* Recalculate GPA Confirm Modal */}
      {showRecalcConfirm && (
        <div className="dash_modal_overlay" role="dialog" aria-modal="true">
          <div className="dash_modal">
            <h3>Recalculate All GPA</h3>
            <p>This will recompute semester GPA, session GPA, and CGPA for every student and semester record in the database.</p>
            <p style={{ color: "#dc2626", fontSize: "0.82rem", marginTop: "0.5rem" }}>This cannot be undone. Continue?</p>
            <div className="dash_modal_actions">
              <button
                className="dash_btn ghost"
                onClick={() => setShowRecalcConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="dash_btn"
                onClick={() => {
                  setShowRecalcConfirm(false);
                  handleRecalculateGpa();
                }}
              >
                Yes, Recalculate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Correct Semester Levels Modal */}
      {showCorrectModal && (
        <div className="dash_modal_overlay" role="dialog" aria-modal="true">
          <div className="dash_modal">
            <h3>Correct Semester Levels</h3>
            <p>Select a session and semester to run the correction.</p>
            <div className="dash_modal_fields">
              <label>Session</label>
              <select
                value={selectedSession}
                onChange={(e) => {
                  setSelectedSession(e.target.value);
                  setSelectedSemester("");
                  setCorrectStatus({ type: "", text: "" });
                }}
              >
                <option value="">Select session</option>
                {sessions.map((s) => (
                  <option key={s.session} value={s.session}>
                    {s.session}
                  </option>
                ))}
              </select>
            </div>
            <div className="dash_modal_fields">
              <label>Semester</label>
              <select
                value={selectedSemester}
                onChange={(e) => setSelectedSemester(e.target.value)}
                disabled={!selectedSession || correcting}
              >
                <option value="">Select semester</option>
                <option value="1">First semester</option>
                <option value="2">Second semester</option>
              </select>
            </div>
            {correctStatus.text && (
              <div className={`dash_modal_status ${correctStatus.type}`}>
                {correctStatus.text}
              </div>
            )}
            <div className="dash_modal_actions">
              <button
                className="dash_btn ghost"
                onClick={() => {
                  setShowCorrectModal(false);
                  setCorrectStatus({ type: "", text: "" });
                }}
                disabled={correcting}
              >
                Cancel
              </button>
              <button
                className="dash_btn"
                onClick={handleCorrectSemesterLevels}
                disabled={!selectedSession || !selectedSemester || correcting}
              >
                {correcting ? "Running..." : "Run Correction"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transcript Settings Modal */}
      {showSettingsModal && (
        <div className="dash_modal_overlay" role="dialog" aria-modal="true">
          <div className="ts_modal">
            <div className="ts_modal_head">
              <div>
                <h3>Transcript Settings</h3>
                <p>
                  Configure the names and signatures printed on faculty
                  transcripts.
                </p>
              </div>
              <button
                className="ts_close_btn"
                onClick={() => {
                  setShowSettingsModal(false);
                  setSettingsStatus({ type: "", text: "" });
                }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="ts_panels">
              {/* Faculty Officer */}
              <div className="ts_panel">
                <p className="ts_panel_label">Faculty Officer</p>
                <div className="ts_field">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={settingsForm.faculty_officer_name}
                    onChange={(e) =>
                      setSettingsForm((p) => ({
                        ...p,
                        faculty_officer_name: e.target.value,
                      }))
                    }
                    placeholder="e.g. Mrs Uchenna C. Ugwu"
                  />
                </div>
                <div className="ts_field">
                  <label>Signature Image</label>
                  <div className="ts_sig_zone">
                    {settings?.faculty_officer_signature ? (
                      <img
                        src={settings.faculty_officer_signature}
                        alt="Faculty Officer Signature"
                        className="ts_sig_img"
                      />
                    ) : (
                      <span className="ts_sig_placeholder">
                        No signature uploaded
                      </span>
                    )}
                    <label
                      className={`ts_upload_btn ${sigUploading.faculty_officer_signature ? "uploading" : ""}`}
                    >
                      {sigUploading.faculty_officer_signature
                        ? "Uploading..."
                        : settings?.faculty_officer_signature
                          ? "Replace"
                          : "Upload"}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        disabled={sigUploading.faculty_officer_signature}
                        onChange={(e) =>
                          handleSignatureUpload(
                            "faculty_officer_signature",
                            e.target.files[0],
                          )
                        }
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="ts_divider" />

              {/* Dean */}
              <div className="ts_panel">
                <p className="ts_panel_label">Dean</p>
                <div className="ts_field">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={settingsForm.dean_name}
                    onChange={(e) =>
                      setSettingsForm((p) => ({
                        ...p,
                        dean_name: e.target.value,
                      }))
                    }
                    placeholder="e.g. Prof. C. S. Nworu"
                  />
                </div>
                <div className="ts_field">
                  <label>Signature Image</label>
                  <div className="ts_sig_zone">
                    {settings?.dean_signature ? (
                      <img
                        src={settings.dean_signature}
                        alt="Dean Signature"
                        className="ts_sig_img"
                      />
                    ) : (
                      <span className="ts_sig_placeholder">
                        No signature uploaded
                      </span>
                    )}
                    <label
                      className={`ts_upload_btn ${sigUploading.dean_signature ? "uploading" : ""}`}
                    >
                      {sigUploading.dean_signature
                        ? "Uploading..."
                        : settings?.dean_signature
                          ? "Replace"
                          : "Upload"}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        disabled={sigUploading.dean_signature}
                        onChange={(e) =>
                          handleSignatureUpload(
                            "dean_signature",
                            e.target.files[0],
                          )
                        }
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {settingsStatus.text && (
              <div className={`dash_modal_status ${settingsStatus.type}`}>
                {settingsStatus.text}
              </div>
            )}

            <div className="ts_modal_footer">
              <button
                className="dash_btn ghost"
                onClick={() => {
                  setShowSettingsModal(false);
                  setSettingsStatus({ type: "", text: "" });
                }}
              >
                Cancel
              </button>
              <button
                className="dash_btn"
                onClick={handleSaveSettings}
                disabled={settingsSaving}
              >
                {settingsSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminDashboard;
